import React, { useEffect, useState } from "react";
import axios from "axios";

import Navbar from "../components/Navbar";
import {API_URL,FAST_API_URL} from "../api"

import {
  CalendarDays,
  Clock3,
  User,
  Phone,
  CircleCheckBig,
  CircleX,
  CircleDashed,
  Stethoscope,
} from "lucide-react";

const PatientAppointments = () => {

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  // =====================================================
  // FETCH APPOINTMENTS
  // =====================================================

  const fetchAppointments = async () => {

    try {

      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${API_URL}/patient-all-appointments`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setAppointments(
        response?.data?.appointments || []
      );

    } catch (error) {

      console.log(error);

    } finally {

      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  // =====================================================
  // STATUS MAP
  // =====================================================

  const statusMap = {
    1: "proposed",
    2: "confirmed",
    3: "delivered",
    4: "cancelled",
    5: "missed",
    6: "completed",
  };

  // =====================================================
  // STATUS UI
  // =====================================================

  const getStatusUI = (status) => {

    switch (status) {

      case "confirmed":
        return {
          color:
            "bg-green-100 text-green-700 border-green-200",
          icon: (
            <CircleCheckBig className="w-4 h-4" />
          ),
        };

      case "cancelled":
        return {
          color:
            "bg-red-100 text-red-700 border-red-200",
          icon: (
            <CircleX className="w-4 h-4" />
          ),
        };

      case "proposed":
        return {
          color:
            "bg-yellow-100 text-yellow-700 border-yellow-200",
          icon: (
            <CircleDashed className="w-4 h-4" />
          ),
        };

      case "completed":
        return {
          color:
            "bg-blue-100 text-blue-700 border-blue-200",
          icon: (
            <CircleCheckBig className="w-4 h-4" />
          ),
        };

      case "missed":
        return {
          color:
            "bg-orange-100 text-orange-700 border-orange-200",
          icon: (
            <CircleX className="w-4 h-4" />
          ),
        };

      default:
        return {
          color:
            "bg-gray-100 text-gray-700 border-gray-200",
          icon: (
            <CircleDashed className="w-4 h-4" />
          ),
        };
    }
  };

  // =====================================================
  // LOADING UI
  // =====================================================

  if (loading) {

    return (

      <>
        <Navbar />

        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 pt-24 p-4">

          <div className="max-w-6xl mx-auto">

            <div className="animate-pulse grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

              {[...Array(6)].map((_, index) => (

                <div
                  key={index}
                  className="bg-white rounded-3xl h-72 shadow-sm"
                />

              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (

    <>
      {/* ================================================= */}
      {/* NAVBAR */}
      {/* ================================================= */}

      <Navbar />

      {/* ================================================= */}
      {/* PAGE */}
      {/* ================================================= */}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 pt-24 pb-10 px-4">

        {/* HEADER */}

        <div className="max-w-7xl mx-auto mb-8">

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

            <div>

              <h1 className="text-3xl font-bold text-gray-800">
                My Appointments
              </h1>

              <p className="text-gray-500 mt-2">
                Track all your doctor appointments easily
              </p>
            </div>

            <div className="bg-white px-5 py-3 rounded-2xl shadow-sm border border-blue-100">

              <p className="text-sm text-gray-500">
                Total Appointments
              </p>

              <h2 className="text-2xl font-bold text-blue-600">
                {appointments.length}
              </h2>
            </div>
          </div>
        </div>

        {/* EMPTY */}

        {appointments.length === 0 ? (

          <div className="max-w-xl mx-auto bg-white rounded-3xl shadow-lg p-10 text-center">

            <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto">

              <CalendarDays className="w-10 h-10 text-blue-600" />
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mt-5">
              No Appointments Found
            </h2>

            <p className="text-gray-500 mt-2">
              You don’t have any appointments yet.
            </p>
          </div>

        ) : (

          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

            {appointments.map((item, index) => {

              // =====================================
              // FIX STATUS LABEL
              // =====================================

              const statusLabel =
                statusMap[
                  Number(
                    item?.appointment_status?.status_code
                  )
                ] || "unknown";

              const statusUI =
                getStatusUI(statusLabel);

              return (

                <div
                  key={index}
                  className="
                    relative
                    bg-white/90
                    backdrop-blur-xl
                    rounded-3xl
                    shadow-lg
                    border border-white
                    overflow-hidden
                    hover:shadow-2xl
                    transition-all duration-300
                    hover:-translate-y-1
                  "
                >

                  {/* TOP STRIP */}

                  <div className="h-2 bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-400" />

                  {/* BODY */}

                  <div className="p-6">

                    {/* STATUS */}

                    <div className="flex justify-between items-start mb-5">

                      <div>

                        <h2 className="text-lg font-bold text-gray-800">
                          Dr. {item?.doctor?.doctor_name}
                        </h2>

                        <p className="text-sm text-gray-500 mt-1">
                          Appointment ID:
                          #{item?.appointment_id}
                        </p>
                      </div>

                      <div
                        className={`
                          flex items-center gap-1
                          px-3 py-1.5
                          rounded-full
                          border
                          text-xs font-semibold capitalize
                          ${statusUI.color}
                        `}
                      >
                        {statusUI.icon}

                        {statusLabel}
                      </div>
                    </div>

                    {/* INFO */}

                    <div className="space-y-4">

                      {/* DOCTOR */}

                      <div className="flex items-center gap-3">

                        <div className="bg-blue-100 p-2 rounded-xl">
                          <Stethoscope className="w-5 h-5 text-blue-600" />
                        </div>

                        <div>

                          <p className="text-xs text-gray-500">
                            Doctor
                          </p>

                          <p className="text-sm font-semibold text-gray-800">
                            {item?.doctor?.doctor_name}
                          </p>
                        </div>
                      </div>

                      {/* PHONE */}

                      <div className="flex items-center gap-3">

                        <div className="bg-green-100 p-2 rounded-xl">
                          <Phone className="w-5 h-5 text-green-600" />
                        </div>

                        <div>

                          <p className="text-xs text-gray-500">
                            Phone
                          </p>

                          <p className="text-sm font-semibold text-gray-800">
                            {item?.doctor?.doctor_phone}
                          </p>
                        </div>
                      </div>

                      {/* DATE */}

                      <div className="flex items-center gap-3">

                        <div className="bg-purple-100 p-2 rounded-xl">
                          <CalendarDays className="w-5 h-5 text-purple-600" />
                        </div>

                        <div>

                          <p className="text-xs text-gray-500">
                            Appointment Date
                          </p>

                          <p className="text-sm font-semibold text-gray-800">
                            {
                              item?.appointment_status
                                ?.event_date
                            }
                          </p>
                        </div>
                      </div>

                      {/* TIME */}

                      <div className="flex items-center gap-3">

                        <div className="bg-orange-100 p-2 rounded-xl">
                          <Clock3 className="w-5 h-5 text-orange-600" />
                        </div>

                        <div>

                          <p className="text-xs text-gray-500">
                            Appointment Time
                          </p>

                          <p className="text-sm font-semibold text-gray-800">
                            {
                              item?.appointment_status
                                ?.event_time
                            }
                          </p>
                        </div>
                      </div>

                      {/* PROPOSED BY */}

                      <div className="flex items-center gap-3">

                        <div className="bg-cyan-100 p-2 rounded-xl">
                          <User className="w-5 h-5 text-cyan-600" />
                        </div>

                        <div>

                          <p className="text-xs text-gray-500">
                            Proposed By
                          </p>

                          <p className="text-sm font-semibold capitalize text-gray-800">
                            {
                              item?.appointment_status
                                ?.proposed_by
                            }
                          </p>
                        </div>
                      </div>

                    </div>

                    {/* FOOTER */}

                    <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between items-center">

                      <p className="text-xs text-gray-400">
                        Created:
                      </p>

                      <p className="text-xs font-medium text-gray-600">
                        {new Date(
                          item?.created_at
                        ).toLocaleString()}
                      </p>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default PatientAppointments;